import React from "react";
import * as Styled from "./styles";
import UserImage from "../UserImage";
import { TextView } from "@components";
import { colors } from "../../utils";
import { useNavigate } from "react-router-dom";
import { resetFolders } from "../../redux/reducers/gallery";
import { useDispatch } from "react-redux";
import { USER_ACTIONS_ENABLED } from "../../constants/featureFlags";
import UserActions from "../UserActions";
import { useReducedMotion } from "framer-motion";
import { searchItemVariants } from "./searchMotion";

export default function UserItem({
  item,
  setSearchText,
  setModalOpen = null,
  setOnLeave = null,
  wheelInformation = null,
  prevIndexRef = null,
  setShowResults = null,
  handleNavigate = null,
  kbdNav = false,
  isHighlighted = false,
  optionId = undefined,
}) {
  const { first_name, last_name, user_id, profile_image, profession ,username} = item;
  const nav = useNavigate();
  const dispatch = useDispatch();

  // Honor OS reduced-motion: no slide-in, hover scale, or tap bounce.
  const reduceMotion = useReducedMotion();
  const itemVariants = searchItemVariants(reduceMotion);

  const handleUserClick = () => {
    setShowResults(false);

    // Call handleNavigate if it exists
    if (handleNavigate) {
      handleNavigate();
      return;
    }

    if (setOnLeave && setModalOpen) {
      const currentIndex = wheelInformation?.currentIndex;

      if (prevIndexRef.current !== currentIndex) {
        setOnLeave(() => () => {
          setSearchText("");
          dispatch(resetFolders());
          nav(`/gallery/${user_id}`);
        });
        setModalOpen(true);
        prevIndexRef.current = currentIndex;
      } else {
        setSearchText("");
        dispatch(resetFolders());
        nav(`/gallery/${user_id}`);
      }
    } else {
      setSearchText("");
      dispatch(resetFolders());
      nav(`/gallery/${user_id}`);
    }
  };
  

  // EW-1182: additive keyboard-nav wiring — a stable click target for the
  // parent's Enter handler, the ARIA option semantics, and a visible brand
  // highlight. All gated on kbdNav so a flag-OFF render is byte-identical.
  const kbdProps = kbdNav
    ? {
        "data-search-result-item": "",
        role: "option",
        id: optionId,
        "aria-selected": isHighlighted,
        style: isHighlighted
          ? {
              backgroundColor: colors.Highlight,
              outline: `2px solid ${colors.PrimaryPurple500}`,
              outlineOffset: "-2px",
            }
          : undefined,
      }
    : {};

  // Identify the person unambiguously (Gordon 2026-07-28: "the search results
  // aren't showing her name! What if there are 10 different Terrys?"). Lead with
  // the DISPLAY NAME (first + last), fall back to the username, then a generic
  // label — a result is never a bare avatar with no text. The username is shown
  // underneath when it adds information (i.e. isn't already the display name).
  //
  // ISP-9999 (kelly-0916-gallery-search-result-at-prefix-not-username): no '@'
  // prefix — MEASURED live on web-dev (main.b7c6f043.js) that the Profile page
  // (components/WheelGallery/Profile/index.jsx) has no '@handle' concept at all;
  // it renders the bare username with no prefix. A stale/renamed test account
  // (user_id 28: first/last "Gordon Maniskas", username still "faye_kautzer27")
  // made the old "@username" line read as an unrelated person's handle. Dropping
  // the '@' keeps this line truthful to what the profile itself shows: a plain
  // username, not a handle that doesn't exist anywhere in the product.
  const fullName = `${first_name || ""} ${last_name || ""}`.trim();
  const displayName = fullName || username || "iYuga user";
  const showHandle = Boolean(username) && username !== displayName;

  return (
    <Styled.SearchItem
      data-testid="search-user-row"
      onClick={handleUserClick}
      variants={itemVariants}
      initial="initial"
      animate="animate"
      whileHover="hover"
      whileTap="tap"
      {...kbdProps}
    >
      {/* ISP-9999 (#965): 48px avatar matches the Chat side-pane row's own
          avatar size (pages/Chat/styles.js AvatarWrap default). */}
      <div style={{ minWidth: 48, flexShrink: 0 }}>
        <UserImage image={profile_image} size={48} />
      </div>
      <Styled.SearchItemDetails>
        {/* QA-1003 (REOPENED, Gordon 2026-08-25): the result row was clamping
            the display name to a SINGLE line, so a full "first last" name whose
            column was crushed by the avatar + inline Follow/Message actions lost
            its LAST name to an ellipsis — MEASURED live on web-stg
            (main.185a846d.js): "Kelly Ellerbrake" rendered as "Kelly …" in a
            ~35px-wide name column. Allow the name up to TWO lines so a long full
            name WRAPS and stays fully readable instead of dropping the surname
            (matches how names wrap elsewhere in the app). The username line below
            stays single-line. */}
        <TextView
          variant="subtitle"
          style={{ fontSize: 14, fontWeight: 600, WebkitLineClamp: 2 }}
        >
          {displayName}
        </TextView>
        {showHandle && (
          <TextView
            color={colors.textGrey}
            style={{ fontSize: 13, WebkitLineClamp: 1 }}
          >
            {username}
          </TextView>
        )}
      </Styled.SearchItemDetails>
      {/* Follow + Message inline to the RIGHT of the name (BUG-search-568021,
          Gordon 2026-08-12): a standard name-left / actions-right row, replacing
          the previous stacked-below line. Flag-OFF => this branch is skipped and
          the row is a plain avatar+name line (byte-identical). The controls stop
          click-propagation so they never trigger the row's navigate-to-profile.
          The two 44px icon buttons keep their natural width (no stretch) so the
          name column ellipsises instead of being crushed. */}
      {USER_ACTIONS_ENABLED && user_id != null && (
        <Styled.UserActionsInline onClick={(e) => e.stopPropagation()}>
          {/* #965: this dropdown is a short panel — a bottom tooltip on the
              last visible row spills past its edge, so it renders above. */}
          <UserActions
            userId={user_id}
            userLabel={username}
            variant="full"
            tooltipPlacement="top"
          />
        </Styled.UserActionsInline>
      )}
    </Styled.SearchItem>
  );
}
